import { useMemo, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";

import { IPart } from "../models/book";
import { IChapter } from "../models/chapter";
import { EGenerationStatus } from "../models/generationStatus";

const byNumber = (first: { number: number }, second: { number: number }) =>
  first.number - second.number;

export const CHAPTER_ROUTES = {
  plan: "/book/$bookId/plan/$partNumber/$chapterNumber",
  content: "/book/$bookId/content/$partNumber/$chapterNumber",
} as const;

export type ChapterSection = keyof typeof CHAPTER_ROUTES;

export const validateAutoGenerateSearch = (
  search: Record<string, unknown>,
) => ({
  autoGenerate: search.autoGenerate === true,
});

export interface ChapterNavItem {
  id: string;
  partNumber: number;
  chapterNumber: number;
  title: string;
  hasPlan: boolean;
  hasContent: boolean;
  summaryStatus: EGenerationStatus | null;
  hasReadySummary: boolean;
  globalNumber: number;
}

export interface PartRow {
  part: IPart;
  chapters: IChapter[];
  chapterOffset: number;
  chapterBeforePartHasPlan: boolean;
}

// Reading order carries two things across a part boundary: the running chapter
// count behind each row's global number, and whether the chapter before a part
// has a plan, which is where that part's lock chain starts.
export const listPartsInReadingOrder = (parts: IPart[]): PartRow[] => {
  let chapterOffset = 0;
  let chapterBeforePartHasPlan = true;

  return parts.toSorted(byNumber).map((part) => {
    const chapters = part.chapters.toSorted(byNumber);
    const row = { part, chapters, chapterOffset, chapterBeforePartHasPlan };

    chapterOffset += chapters.length;
    const lastChapter = chapters.at(-1);
    if (lastChapter) chapterBeforePartHasPlan = Boolean(lastChapter.plan);

    return row;
  });
};

export const listChaptersInReadingOrder = (parts: IPart[]): ChapterNavItem[] =>
  parts
    .toSorted((first, second) => first.number - second.number)
    .flatMap((part) =>
      part.chapters
        .toSorted((first, second) => first.number - second.number)
        .map((chapter) => ({
          id: chapter.id,
          partNumber: part.number,
          chapterNumber: chapter.number,
          title: chapter.title,
          hasPlan: Boolean(chapter.plan),
          hasContent: Boolean(chapter.content),
          summaryStatus: chapter.summary_status ?? null,
          hasReadySummary:
            Boolean(chapter.summary?.trim()) &&
            chapter.summary_status === EGenerationStatus.SUCCESS,
        })),
    )
    .map((chapter, index) => ({ ...chapter, globalNumber: index + 1 }));

export const findBlockingSummaryChapter = (
  chapters: ChapterNavItem[],
  currentIndex: number,
): ChapterNavItem | null =>
  chapters
    .slice(0, Math.max(currentIndex, 0))
    .find((chapter) => chapter.hasContent && !chapter.hasReadySummary) ?? null;

interface UseChapterNavigationProps {
  bookId: string;
  partNumber: number;
  chapterNumber: number;
  parts: IPart[];
  basePath: ChapterSection;
}

interface UseChapterNavigationResult {
  allChapters: ChapterNavItem[];
  currentIndex: number;
  currentGlobalNumber: number | null;
  prevChapter: ChapterNavItem | null;
  nextChapter: ChapterNavItem | null;
  navigateToChapter: (partNumber: number, chapterNumber: number) => void;
}

export function useChapterNavigation({
  bookId,
  partNumber,
  chapterNumber,
  parts,
  basePath,
}: UseChapterNavigationProps): UseChapterNavigationResult {
  const navigate = useNavigate();

  const allChapters = useMemo(() => listChaptersInReadingOrder(parts), [parts]);

  const currentIndex = useMemo(() => {
    return allChapters.findIndex(
      (c) => c.partNumber === partNumber && c.chapterNumber === chapterNumber,
    );
  }, [allChapters, partNumber, chapterNumber]);

  const currentGlobalNumber =
    currentIndex >= 0 ? allChapters[currentIndex]?.globalNumber : null;

  const prevChapter = currentIndex > 0 ? allChapters[currentIndex - 1] : null;
  const nextChapter =
    currentIndex >= 0 && currentIndex < allChapters.length - 1
      ? allChapters[currentIndex + 1]
      : null;

  const navigateToChapter = useCallback(
    (pNum: number, cNum: number) => {
      navigate({
        to: CHAPTER_ROUTES[basePath],
        params: {
          bookId,
          partNumber: String(pNum),
          chapterNumber: String(cNum),
        },
        search: { autoGenerate: false },
      });
    },
    [navigate, bookId, basePath],
  );

  return {
    allChapters,
    currentIndex,
    currentGlobalNumber,
    prevChapter,
    nextChapter,
    navigateToChapter,
  };
}
